import { ApiService } from "@core/api/api-service";
import { BaseExercise, Exercise, ExerciseComment, MainExercise } from "@domain/exercise/exercise";
import { v4 as uuid } from "uuid";

export type MainExerciseDto = Omit<Exercise, "draggableId" | "alternativeExercises"> & {
	alternativeExercises: AlternativeExerciseDto[];
};
type AlternativeExerciseDto = Omit<BaseExercise, "draggableId">;
type ExerciseDto = Omit<MainExerciseDto, "units" | "setCount">;

export interface MainExerciseLightDto {
	id: string;
	revision: number;
	units: number;
	setCount: number;
	comment?: ExerciseComment;
	alternativeExercises: AlternativeExerciseLightDto[];
}

type AlternativeExerciseLightDto = Omit<MainExerciseLightDto, "alternativeExercises">;

export class ExerciseService {
	constructor(private readonly apiService: ApiService) {}

	async listExercises(): Promise<BaseExercise[]> {
		const result = await this.apiService.get<ExerciseDto[]>("/library/exercises");
		return result.data.map(exercise => ExerciseService.exerciseFromLibraryExerciseDto(exercise));
	}

	private static exerciseFromLibraryExerciseDto(exerciseDto: ExerciseDto): BaseExercise {
		return {
			...exerciseDto,
			units: 10,
			setCount: 1,
			draggableId: uuid(),
		};
	}

	static exerciseFromDto(exerciseDto: MainExerciseDto): MainExercise {
		return {
			...exerciseDto,
			draggableId: uuid(),
			alternativeExercises: exerciseDto.alternativeExercises.map(ExerciseService.alternativeExerciseFromDto),
		};
	}

	static dtoFromExercise(exercise: MainExercise): MainExerciseLightDto {
		return {
			id: exercise.id,
			revision: exercise.revision,
			units: exercise.units,
			setCount: exercise.setCount,
			comment: exercise.comment,
			alternativeExercises: exercise.alternativeExercises.map(this.dtoFromAlternativeExercise),
		};
	}

	static dtoFromAlternativeExercise(alternativeExercise: BaseExercise): AlternativeExerciseLightDto {
		return {
			id: alternativeExercise.id,
			revision: alternativeExercise.revision,
			units: alternativeExercise.units,
			setCount: alternativeExercise.setCount,
			comment: alternativeExercise.comment,
		};
	}

	private static alternativeExerciseFromDto(alternativeDto: AlternativeExerciseDto): BaseExercise {
		return { ...alternativeDto, draggableId: uuid() };
	}
}
